/**
 * ShareDialog — "Share with people" modal, shared by Notes and Canvas.
 *
 * Current recipients are listed with a "Can edit" switch (read-only ↔ edit) and
 * a remove button; colleagues are added from a searchable checkbox list and
 * start read-only. The caller supplies the data calls for its own resource:
 *
 *   api = {
 *     listColleagues: ()                    => [{ id, name, email }]
 *     getShares:      ()                    => [{ user_id, name, email, permission }]
 *     share:          (userIds)             => any
 *     setPermission:  (userId, 'view'|'edit') => any
 *     unshare:        (userId)              => any
 *   }
 *
 * Sizes, margins and line-heights are set explicitly throughout: the global
 * `button` / `input` rules in index.css pin controls to 40px with side padding.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Share2, Search, X } from "lucide-react";
import { T } from "./tokens";
import { PrimaryButton, SecondaryButton, ModalOverlay, Avatar } from "./ui";
import { toast } from "../../utils/toast";

export const permissionText = (p) => (p === "edit" ? "Can view and edit" : "Can view only");

// "Can edit" label + on/off switch. value is 'view' | 'edit'.
export function PermissionSwitch({ value, onChange, disabled = false }) {
  const on = value === "edit";
  return (
    <label
      style={{
        display: "inline-flex", alignItems: "center", gap: 10, flexShrink: 0,
        cursor: disabled ? "default" : "pointer", font: `500 13px ${T.font}`,
        color: T.fg2, whiteSpace: "nowrap",
      }}
    >
      Can edit
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label="Can edit"
        disabled={disabled}
        onClick={() => onChange(on ? "view" : "edit")}
        style={{
          width: 44, height: 24, minWidth: 44, margin: 0, padding: 2, lineHeight: 0,
          border: 0, borderRadius: 9999, cursor: disabled ? "default" : "pointer",
          background: on ? T.orange : T.border, transition: "background 180ms ease-out",
          display: "inline-flex", alignItems: "center", flexShrink: 0,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span
          style={{
            display: "block", width: 20, height: 20, borderRadius: 9999, background: "#fff",
            transform: on ? "translateX(20px)" : "translateX(0)",
            transition: "transform 180ms ease-out", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}
        />
      </button>
    </label>
  );
}

const sectionLabel = {
  font: `700 10.5px ${T.font}`, color: T.fg6 || T.fg5, textTransform: "uppercase",
  letterSpacing: "0.07em", marginBottom: 8,
};
const personRow = { display: "flex", alignItems: "center", gap: 10, padding: "7px 8px", borderRadius: T.rMd };
const personName = {
  font: `600 13px/1.3 ${T.font}`, color: T.fg1, overflow: "hidden",
  textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const personEmail = {
  font: `400 11.5px/1.3 ${T.font}`, color: T.fg5, overflow: "hidden",
  textOverflow: "ellipsis", whiteSpace: "nowrap",
};

export default function ShareDialog({ title = "Share", subtitle, api, onClose, onCountChange }) {
  const [colleagues, setColleagues] = useState([]);
  const [shares, setShares] = useState([]);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cols, current] = await Promise.all([api.listColleagues(), api.getShares()]);
        if (!alive) return;
        setColleagues(Array.isArray(cols) ? cols : []);
        setShares(Array.isArray(current) ? current : []);
      } catch (e) {
        console.error("Failed to load sharing data", e);
        if (alive) toast.error(e?.response?.data?.error || "Could not load sharing.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // `api` is rebuilt by callers on every render; load once per dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recipientIds = useMemo(() => new Set(shares.map((s) => s.user_id)), [shares]);

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return colleagues.filter(
      (c) =>
        !recipientIds.has(c.id) &&
        (!q || (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q)),
    );
  }, [colleagues, recipientIds, query]);

  const togglePick = (id) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const doShare = async () => {
    if (!picked.length || busy) return;
    setBusy(true);
    try {
      // New people start read-only; the per-person switch grants edit.
      await api.share(picked);
      const current = await api.getShares();
      setShares(current);
      setPicked([]);
      onCountChange?.(current.length);
    } catch (e) {
      console.error("Share failed", e);
      toast.error(e?.response?.data?.error || "Could not share.");
    } finally {
      setBusy(false);
    }
  };

  // Optimistic flip; rolled back if the server refuses.
  const changePermission = async (userId, permission) => {
    const prev = shares;
    setShares((list) => list.map((s) => (s.user_id === userId ? { ...s, permission } : s)));
    try {
      await api.setPermission(userId, permission);
    } catch (e) {
      console.error("Permission change failed", e);
      setShares(prev);
      toast.error(e?.response?.data?.error || "Could not change permission.");
    }
  };

  const removeShare = async (userId) => {
    try {
      await api.unshare(userId);
      const next = shares.filter((s) => s.user_id !== userId);
      setShares(next);
      onCountChange?.(next.length);
    } catch (e) {
      console.error("Unshare failed", e);
      toast.error(e?.response?.data?.error || "Could not remove access.");
    }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 500, maxWidth: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column",
          background: "var(--em-bg-card)", borderRadius: T.rXl, overflow: "hidden",
          boxShadow: T.shadowMd || T.shadowSm,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 22px", borderBottom: `1px solid ${T.divider}` }}>
          <div style={{ width: 36, height: 36, borderRadius: T.rLg, background: T.orange50, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Share2 size={16} color={T.orange} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>{title}</div>
            {subtitle && <div style={{ ...personEmail, marginTop: 1 }}>{subtitle}</div>}
          </div>
          <button
            type="button"
            title="Close"
            onClick={onClose}
            style={{
              width: 28, height: 28, minWidth: 28, margin: 0, padding: 0, lineHeight: 0,
              borderRadius: T.rSm, border: `1px solid ${T.border}`, background: "transparent",
              color: T.fg4, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={13} style={{ width: 13, height: 13, top: 0, margin: 0 }} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 22px", display: "flex", flexDirection: "column", gap: 16 }}>
          {loading ? (
            <div style={{ padding: 20, font: `400 13px ${T.font}`, color: T.fg5, textAlign: "center" }}>Loading…</div>
          ) : (
            <>
              {shares.length > 0 && (
                <div>
                  <div style={sectionLabel}>Shared with</div>
                  {shares.map((s) => (
                    <div key={s.user_id} style={personRow}>
                      <Avatar name={s.name} size={28} />
                      {/* Subtitle states the access; email stays on hover. */}
                      <div style={{ flex: 1, minWidth: 0 }} title={s.email}>
                        <div style={personName}>{s.name}</div>
                        <div style={personEmail}>{permissionText(s.permission)}</div>
                      </div>
                      <PermissionSwitch
                        value={s.permission === "edit" ? "edit" : "view"}
                        onChange={(p) => changePermission(s.user_id, p)}
                      />
                      <button
                        type="button"
                        title="Remove access"
                        aria-label={`Remove ${s.name}`}
                        onClick={() => removeShare(s.user_id)}
                        style={{
                          width: 28, height: 28, minWidth: 28, margin: "0 0 0 6px", padding: 0, lineHeight: 0,
                          borderRadius: T.rSm, cursor: "pointer", border: 0, background: "transparent",
                          color: T.fg7, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}
                      >
                        <X size={16} style={{ width: 16, height: 16, top: 0 }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <div style={sectionLabel}>Add people</div>
                <div style={{ position: "relative" }}>
                  <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.fg7 }} />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name or email…"
                    style={{
                      width: "100%", height: 38, margin: 0, padding: "0 12px 0 32px", boxSizing: "border-box",
                      border: `1px solid ${T.border}`, borderRadius: T.rMd, background: "var(--em-bg-card)",
                      font: `400 13px ${T.font}`, color: T.fg1, outline: "none",
                    }}
                  />
                </div>
                <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 8 }}>
                  {candidates.length === 0 ? (
                    <div style={{ padding: "14px 8px", font: `400 12.5px ${T.font}`, color: T.fg5 }}>
                      {query ? "No colleagues match your search." : "Everyone available already has access."}
                    </div>
                  ) : (
                    candidates.map((c) => {
                      const on = picked.includes(c.id);
                      return (
                        <label key={c.id} style={{ ...personRow, cursor: "pointer", background: on ? T.orange50 : "transparent" }}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => togglePick(c.id)}
                            style={{ width: 15, height: 15, accentColor: T.orange, cursor: "pointer", flexShrink: 0 }}
                          />
                          <Avatar name={c.name} size={28} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={personName}>{c.name}</div>
                            <div style={personEmail}>{c.email}</div>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, padding: "12px 22px", borderTop: `1px solid ${T.divider}`, background: T.bgHead }}>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
          <PrimaryButton
            onClick={doShare}
            disabled={!picked.length || busy}
            style={!picked.length || busy ? { opacity: 0.55, cursor: "default" } : {}}
          >
            {busy ? "Sharing…" : `Share${picked.length ? ` (${picked.length})` : ""}`}
          </PrimaryButton>
        </div>
      </div>
    </ModalOverlay>
  );
}
